import { LitElement, html } from 'lit';
import { property, state, customElement, queryAsync } from 'lit/decorators.js';
import { GoonAPIClient, Post, User as UserProfile } from '../client/ApiClient';
import { API_ENDPOINT } from '../constants';
import { consume } from '@lit/context';
import { authContext, AuthState } from '../context/AuthContext';

const api = new GoonAPIClient(API_ENDPOINT)

@customElement('channel-feed')
export class ChannelFeed extends LitElement {
  createRenderRoot() { return this }

  @property() channelSlug = '';
  @property() channelName = '';
  @property() channelImageUrl = '';
  @property() channelId = '';

  @state() posts: Post[] = [];
  @state() creators: UserProfile[] = [];
  @state() page = 1;
  @state() loading = false;
  @state() done = false;
  observer;
  @state() showCreatePost = false;
  @state() joined = false;

  @queryAsync('sort-by-filter') filterEl!: Promise<any>;

  @consume({ context: authContext, subscribe: true })
  auth!: AuthState;

  async connectedCallback() {
    super.connectedCallback();
    this.addEventListener('share-in-chat', e => {
      this.shadowRoot?.querySelector('realtime-chat')?.addPostToDraft?.(e.detail.postId);
    });
  }

  firstUpdated() {
    this.sentinel = this.renderRoot.querySelector('#feed-sentinel');
    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      rootMargin: '30px'
    });
    this.observer.observe(this.sentinel);

    this.load();
    this.checkJoinStatus()
  }

  async load() {
    if (this.loading || this.done) return;
    this.loading = true;
    try {
      const filter = await this.filterEl;
      const sort = filter?.sort;
      const range = sort === 'top' ? filter?.timeframe : undefined;

      const response = await api.getPaginatedChannelPosts(this.channelSlug, this.page, sort, range);
      if (!response.posts.length) {
        this.done = true;
        return;
      }
      this.posts = [...this.posts, ...response.posts];
      this.creators = [...this.creators, ...response.users];
      this.page++;
    } catch (err) {
      console.error("Failed to load posts", err);
      this.done = true;
    } finally {
      this.loading = false;
    }
  }

  async reload() {
    this.page = 1;
    this.done = false;
    this.posts = [];
    this.creators = [];
    await this.load();
  }

  handleIntersect(entries) {
    if (entries.some(e => e.isIntersecting)) {
      this.load();
    }
  }

  async checkAuthOrPrompt() {
    if (!this.auth.userId) {
      this.dispatchEvent(new CustomEvent('open-auth-modal', { bubbles: true, composed: true }));
      return false;
    }
    return true;
  }

  async checkJoinStatus() {
    if (!this.auth.userId) return;
    try {
      this.joined = await api.checkUserJoinedChannel(this.channelSlug);
    } catch (err) {
      console.error('Failed to check join status', err);
    }
  }

  async handleJoinOrLeave() {
    if (!(await this.checkAuthOrPrompt())) return;
    try {
      if (this.joined) {
        await api.leaveChannel(this.channelSlug);
        this.joined = false;
      } else {
        await api.joinChannel(this.channelSlug);
        this.joined = true;
      }
    } catch (err) {
      console.error('Channel join/leave failed', err);
    }
    this.requestUpdate();
  }

  async handleCreatePost() {
    if (!(await this.checkAuthOrPrompt())) return;
    this.showCreatePost = true
    this.requestUpdate();
  }

  handlePostDeleted(e: CustomEvent) {
    const idToRemove = e.detail.postId;
    this.posts = this.posts.filter(p => p.id !== idToRemove);
  }

  renderEmptyCTA() {
    return html`
      <div class="card bg-base-300 p-6 text-center shadow">
        <h2 class="text-xl font-bold mb-2">No posts yet</h2>
        <p class="text-base-content mb-4">Be the first to share something in #${this.channelName}</p>
        <button class="btn btn-primary" @click=${this.handleCreatePost}>Create First Post</button>
      </div>
    `;
  }

  render() {
    return html`
      <div class="flex flex-col lg:flex-row gap-6 px-4 sm:px-6 py-6 mx-auto w-full">
        <div class="w-full lg:w-4/6">
          <div class="bg-base-300 card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 p-4">
            <!-- Left: Channel Info -->
            <div class="flex flex-col sm:flex-row gap-4 sm:items-center">
              <img class="w-24 h-24 object-cover rounded-full" src=${this.channelImageUrl} />
              <h1 class="text-xl sm:text-2xl font-bold text-primary">#${this.channelName}</h1>
              <sort-by-filter @filter-change=${this.reload}></sort-by-filter>
            </div>

            <!-- Right: Buttons -->
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
              <button
                @click=${this.handleJoinOrLeave}
                class="btn btn-outline btn-sm sm:btn-md plausible-event-name=feed-${this.joined ? 'leave' : 'join'}-click">
                ${this.joined ? 'Leave' : 'Join'}
              </button>
              <button
                @click=${this.handleCreatePost}
                class="btn btn-primary btn-md plausible-event-name=feed-create-post-click">
                Create Post
              </button>
            </div>
          </div>

          <main class="flex flex-col gap-4">
            ${this.posts.length === 0 && this.done
        ? this.renderEmptyCTA()
        : this.posts.map(post => {
          const creator = this.creators.find(c => c.id === post.user_profile_id)
          if (creator) {
            return html`<post-card
              .post=${post}
              .creator=${creator}
              @post-deleted=${this.handlePostDeleted}
            ></post-card>`
          }
        })}
            <div id="feed-sentinel" class="h-12"></div>
          </main>
        </div>

        <aside class="w-full lg:w-2/6">
          <div class="sticky top-20">
            <realtime-chat .channelSlug=${this.channelSlug}></realtime-chat>
          </div>
        </aside>
      </div>

      <create-post-modal
        .channelSlug=${this.channelSlug}
        .channelId=${this.channelId}
        .open=${this.showCreatePost}
        @close=${() => this.showCreatePost = false}
      ></create-post-modal>
    `;
  }
}

